import { BrewManError } from "@/app/errors";
import { EnumDocumentContextType } from "@/app/services/generated/enums/enumDocumentContextType.generated";
import { EnumSalesVisitAssociatedEntityType } from "@/app/services/generated/enums/enumSalesVisitAssociatedEntityType.generated";
import type { Group } from "@/app/services/generated/groupService";
import type { Lead } from "@/app/services/generated/leadService";

import type { OutletSummary } from "@/app/services/generated/outletService";
import type { SalesVisitLog } from "@/app/services/generated/salesvisitService";
import {
  PlannedSalesVisit,
  PlannedSalesVisitFilter,
  SalesVisitAssociatedEntityFilter,
  salesVisitService,
} from "@/app/services/generated/salesvisitService";
import Guid from "@/app/types/common/guid";
import { LocalTime, type LocalDate } from "@js-joda/core";
import messages, { keys } from "@/plugins/i18n";
import { EnumBmEntityType } from "@/app/services/generated/enums/enumBmEntityType.generated";
import { contactsMenu } from "@/app/ui/navigation/contactsMenu";
import { salesMenu } from "@/app/ui/navigation/salesMenu";
import { showConfirmationDialog } from "@/components/composables/promptDialogs";
import { EnumDialogType } from "@/components/dialogs/DialogTypes";
import { customColoursLight } from "@/plugins/vuetify";
import type { Location } from "vue-router";
import { showModalDialog } from "@/components/platform/common/showModalDialog";
import CompleteVisitDialog from "@/components/pages/orders/salesvisits/components/CompleteVisitDialog.vue";
import { openMapView } from "@da/components/mixins/BmExternalLinks";
import type Key from "@/app/types/common/key";
import type { IHasGroupsCache } from "@/app/caching/caches/groupsCache";
import type { IHasOutletSummariesCache } from "@/app/caching/caches/outletSummariesCache";
import type { IHasLeadsCache } from "@/app/caching/caches/leadsCache";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import tenantStore from "@/store/modules/tenantStore";
import type { Maybe } from "@/components/utils/typeHelpers";

type CacheOutletsGroupsLeads = IHasOutletSummariesCache &
  IHasGroupsCache &
  IHasLeadsCache;

export type SalesVisitWithEntity = {
  salesVisit: PlannedSalesVisit;
  entity: AssociatedEntityDetails;
  sequence?: number;
};

export interface AssociatedEntityDetails {
  type: EnumSalesVisitAssociatedEntityType;
  id: Guid;
  name: string;
  code: string | null;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  addressLine4: string;
  addressLine5: string;
  addressLine6: string;
  addressPostcode: string;
  telephoneNumber: string;
  emailAddress: string;
  salesAreaId: Guid | null;
  salesCodeId: Guid | null;
  latitude: number | null;
  longitude: number | null;
  outletTypeId: Guid | null;
  onHold: boolean | null;
  group?: Group;
  lead?: Lead;
  outlet?: OutletSummary;
}

type SalesVistitEntityMapEntry = {
  salesVisitEntity: EnumSalesVisitAssociatedEntityType;
  colour: string;
  viewLocation: (id: Maybe<Guid>) => Location | undefined;
  documentContext: EnumDocumentContextType | undefined;
  bmEntityType: EnumBmEntityType;
  activityLogType: EnumBmEntityType | null;
  typeNameKey: Key;
  getEntityDetails: (
    visit: PlannedSalesVisit,
    cache: CacheOutletsGroupsLeads
  ) => AssociatedEntityDetails;
};

const outletMapEntry: SalesVistitEntityMapEntry = {
  salesVisitEntity: EnumSalesVisitAssociatedEntityType.outlet,
  colour: customColoursLight.mediumBlue,
  viewLocation: (id: Maybe<Guid>) =>
    id && !id.isEmpty() ? contactsMenu.viewOutlet(id) : undefined,
  documentContext: EnumDocumentContextType.outlet,
  bmEntityType: EnumBmEntityType.outlet,
  activityLogType: EnumBmEntityType.outlet,
  typeNameKey: keys.sales_visit_outlet,
  getEntityDetails: (
    visit: PlannedSalesVisit,
    cache: CacheOutletsGroupsLeads
  ) =>
    visit.associatedEntityId
      ? getDetailsFromOutlet(cache.outlets.forId(visit.associatedEntityId))
      : getDetailsForAdHoc(visit),
};

const groupMapEntry: SalesVistitEntityMapEntry = {
  salesVisitEntity: EnumSalesVisitAssociatedEntityType.group,
  colour: customColoursLight.darkBlue,
  viewLocation: (id: Maybe<Guid>) =>
    id && !id.isEmpty() ? contactsMenu.viewOutletGroup(id) : undefined,
  documentContext: EnumDocumentContextType.outletGroup,
  bmEntityType: EnumBmEntityType.outletGroup,
  activityLogType: null,
  typeNameKey: keys.sales_visit_group,
  getEntityDetails: (
    visit: PlannedSalesVisit,
    cache: CacheOutletsGroupsLeads
  ) =>
    visit.associatedEntityId
      ? getDetailsFromGroup(cache.groups.forId(visit.associatedEntityId))
      : getDetailsForAdHoc(visit),
};

const leadMapEntry: SalesVistitEntityMapEntry = {
  salesVisitEntity: EnumSalesVisitAssociatedEntityType.lead,
  colour: customColoursLight.lightBlue,
  viewLocation: (id: Maybe<Guid>) =>
    id && !id.isEmpty() ? salesMenu.leads.viewLead(id) : undefined,
  documentContext: EnumDocumentContextType.lead,
  bmEntityType: EnumBmEntityType.lead,
  activityLogType: EnumBmEntityType.lead,
  typeNameKey: keys.sales_visit_lead,
  getEntityDetails: (
    visit: PlannedSalesVisit,
    cache: CacheOutletsGroupsLeads
  ) =>
    visit.associatedEntityId
      ? getDetailsFromLead(cache.leads.forId(visit.associatedEntityId))
      : getDetailsForAdHoc(visit),
};

const adHocMapEntry: SalesVistitEntityMapEntry = {
  salesVisitEntity: EnumSalesVisitAssociatedEntityType.none,
  colour: customColoursLight.greyChip,
  viewLocation: (id: Maybe<Guid>) => undefined,
  documentContext: undefined,
  bmEntityType: EnumBmEntityType.none,
  activityLogType: null,
  typeNameKey: keys.sales_visit,
  getEntityDetails: (visit: PlannedSalesVisit) => getDetailsForAdHoc(visit),
};

const allMapEntries = [
  outletMapEntry,
  groupMapEntry,
  leadMapEntry,
  adHocMapEntry,
];

export function getSalesVisitEntityMapping(
  type: EnumSalesVisitAssociatedEntityType
) {
  return (
    allMapEntries.find((x) => x.salesVisitEntity.key === type.key) ??
    adHocMapEntry
  );
}

export function getSalesVisitEntityMappingFromBmEntityType(
  type: EnumBmEntityType
) {
  return (
    allMapEntries.find((x) => x.bmEntityType.key === type.key) ?? adHocMapEntry
  );
}

export function getDetailsForAdHoc(
  visit: PlannedSalesVisit
): AssociatedEntityDetails {
  return {
    type: EnumSalesVisitAssociatedEntityType.none,
    // Visit Id is unique and a decent identifier to use.
    id: visit.id,
    name: visit.adHocBusinessName,
    code: null,
    addressLine1: visit.adHocAddressLine1,
    addressLine2: visit.adHocAddressLine2,
    addressLine3: visit.adHocAddressLine3,
    addressLine4: visit.adHocAddressLine4,
    addressLine5: visit.adHocAddressLine5,
    addressLine6: visit.adHocAddressLine6,
    addressPostcode: visit.adHocAddressPostcode,
    telephoneNumber: "",
    emailAddress: "",
    salesAreaId: null,
    salesCodeId: null,
    latitude: visit.readOnly.adHocLatitude,
    longitude: visit.readOnly.adHocLongitude,
    outletTypeId: null,
    onHold: false,
  };
}

export function getDetailsFromOutlet(
  outlet: OutletSummary
): AssociatedEntityDetails {
  return {
    type: EnumSalesVisitAssociatedEntityType.outlet,
    id: outlet.id,
    name: outlet.displayName,
    code: outlet.code,
    addressLine1: outlet.mainAddressLine1,
    addressLine2: outlet.mainAddressLine2,
    addressLine3: outlet.mainAddressLine3,
    addressLine4: outlet.mainAddressLine4,
    addressLine5: outlet.mainAddressLine5,
    addressLine6: outlet.mainAddressLine6,
    addressPostcode: outlet.mainAddressPostcode,
    telephoneNumber: outlet.telephoneNumber,
    emailAddress: outlet.emailAddress,
    salesAreaId: outlet.salesAreaId,
    salesCodeId: outlet.salesCodeId,
    latitude: outlet.mainLatitude,
    longitude: outlet.mainLongitude,
    outletTypeId: outlet.outletTypeId,
    onHold: !!outlet.placedOnHoldAt,
    outlet,
  };
}

export function getDetailsFromGroup(group: Group): AssociatedEntityDetails {
  return {
    type: EnumSalesVisitAssociatedEntityType.group,
    id: group.id,
    name: group.name,
    code: group.readOnly.code,
    addressLine1: group.mainAddressLine1,
    addressLine2: group.mainAddressLine2,
    addressLine3: group.mainAddressLine3,
    addressLine4: group.mainAddressLine4,
    addressLine5: group.mainAddressLine5,
    addressLine6: group.mainAddressLine6,
    addressPostcode: group.mainAddressPostcode,
    telephoneNumber: group.telephoneNumber,
    emailAddress: group.emailAddress,
    salesAreaId: null,
    salesCodeId: group.salesCodeId,
    latitude: group.readOnly.mainLatitude,
    longitude: group.readOnly.mainLongitude,
    outletTypeId: null,
    onHold: group.onHold,
    group,
  };
}

export function getDetailsFromLead(lead: Lead): AssociatedEntityDetails {
  return {
    type: EnumSalesVisitAssociatedEntityType.lead,
    id: lead.id,
    name: lead.businessName,
    code: null,
    addressLine1: lead.addressLine1,
    addressLine2: lead.addressLine2,
    addressLine3: lead.addressLine3,
    addressLine4: lead.addressLine4,
    addressLine5: lead.addressLine5,
    addressLine6: lead.addressLine6,
    addressPostcode: lead.addressPostcode,
    telephoneNumber: lead.telephoneNumber,
    emailAddress: lead.emailAddress,
    salesAreaId: lead.salesAreaId,
    salesCodeId: lead.salesCodeId,
    latitude: lead.readOnly.latitude,
    longitude: lead.readOnly.longitude,
    outletTypeId: lead.outletTypeId,
    onHold: null,
    lead,
  };
}

export function getSalesVisitEntityColour(
  entityType: EnumSalesVisitAssociatedEntityType
) {
  return getSalesVisitEntityMapping(entityType).colour;
}

export function salesVisitViewEntityLocation(visit: {
  associatedEntityType: EnumSalesVisitAssociatedEntityType;
  associatedEntityId: Maybe<Guid>;
}) {
  return getSalesVisitEntityMapping(visit.associatedEntityType).viewLocation(
    visit.associatedEntityId
  );
}

export function joinPlannedVisitWithAssociatedEntity(
  visit: PlannedSalesVisit,
  cache: CacheOutletsGroupsLeads,
  sequence?: number
): SalesVisitWithEntity {
  return {
    salesVisit: visit,
    entity: getSalesVisitEntityMapping(
      visit.associatedEntityType
    ).getEntityDetails(visit, cache),
    sequence: visit.readOnly.doesFitInRoute ? sequence : undefined,
  };
}

export async function deleteVisit(id: Guid) {
  const shouldDeleteVisit = await showConfirmationDialog(
    messages.prompt_confirm_title,
    messages.sales_visit_delete_visit_prompt,
    {
      type: EnumDialogType.error,
    }
  );

  if (shouldDeleteVisit) {
    await salesVisitService.deletePlannedSalesVisit(id);
  }

  return shouldDeleteVisit;
}

export async function deleteVisitLog(id: Guid) {
  const isConfirmed = await showConfirmationDialog(
    messages.prompt_confirm_title,
    messages.sales_visit_delete_visit_log_prompt,
    {
      type: EnumDialogType.error,
    }
  );

  if (isConfirmed) {
    await salesVisitService.deleteSalesVisitLog(id);
  }

  return isConfirmed;
}

export function createVisitLogFromVisitLocation(visit: PlannedSalesVisit) {
  return salesMenu.salesVisits.logs.createLocation({
    entityType: visit.associatedEntityType,
    entityId: visit.associatedEntityId,
    date: visit.date,
    time: visit.plannedTime,
    adHocBusinessName: visit.adHocBusinessName,
    userId: visit.userId,
  });
}

export async function completeSalesVisitLog(
  plannedSalesVisit: PlannedSalesVisit | null
) {
  if (plannedSalesVisit === null) {
    throw new BrewManError(messages.sales_visit_planned_sales_visit_not_found);
  }

  if (plannedSalesVisit?.repeatBasis.isNone()) {
    if (
      await showConfirmationDialog(
        messages.prompt_confirm_title,
        messages.sales_visit_complete_confirm
      )
    ) {
      await salesVisitService.deletePlannedSalesVisit(plannedSalesVisit.id);
    }
  } else {
    await showModalDialog<void, typeof CompleteVisitDialog>(
      CompleteVisitDialog,
      {
        props: {
          plannedSalesVisitId: plannedSalesVisit!.id,
        },
      }
    );
  }
}

export function openVisitNavigation(
  salesVisitWithEntity: SalesVisitWithEntity
) {
  if (
    salesVisitWithEntity.entity.latitude === null ||
    salesVisitWithEntity.entity.longitude == null
  ) {
    openMapView(
      salesVisitWithEntity.entity.addressLine2,
      salesVisitWithEntity.entity.addressLine3,
      salesVisitWithEntity.entity.addressPostcode
    );
    return;
  }
  openMapView(
    salesVisitWithEntity.entity.latitude.toString(),
    salesVisitWithEntity.entity.longitude.toString()
  );
}

export async function getPlannedVisitFromVisitLog(
  salesVisitLog: SalesVisitLog
) {
  const entityType = salesVisitLog.header.associatedEntityType;
  const entityId = salesVisitLog.header.associatedEntityId;
  if (!entityType.isNone() && entityId && !entityId.isEmpty()) {
    const filter = new PlannedSalesVisitFilter();
    filter.fromDate = tenantService.instantToDate(
      salesVisitLog.header.timestamp
    );
    filter.toDate = filter.fromDate;

    const entityFilter = new SalesVisitAssociatedEntityFilter();
    entityFilter.entityId = entityId;
    entityFilter.entityType = entityType;

    filter.limitAssociatedEntities = [entityFilter];
    filter.limitUserIds = [salesVisitLog.header.userId];

    const result =
      await salesVisitService.getPlannedSalesVisitsByFilter(filter);

    return result.matchingCount === 1
      ? result.plannedSalesVisits[0]
      : undefined;
  }
  return undefined;
}

export async function getPlannedVisitByDateAndUser(
  fromDate: LocalDate,
  toDate: LocalDate,
  userIds: readonly Guid[]
) {
  const filter = new PlannedSalesVisitFilter();
  filter.fromDate = fromDate;
  filter.toDate = toDate;
  filter.limitUserIds = userIds;

  return await salesVisitService.getPlannedSalesVisitsByFilter(filter);
}

export type SalesVisitEntityOption = {
  id: Guid;
  name: string;
  isHidden: boolean;
};
export function getSalesVisitEntityOptions(
  type: EnumSalesVisitAssociatedEntityType,
  cache: IHasGroupsCache & IHasOutletSummariesCache & IHasLeadsCache
): SalesVisitEntityOption[] {
  if (type.isOutlet()) {
    return cache.outlets.all.map((x) => ({
      id: x.id,
      name: x.displayName,
      isHidden: x.isHidden,
    }));
  } else if (type.isGroup()) {
    return cache.groups.all.map((x) => ({
      id: x.id,
      name: x.name,
      isHidden: x.isHidden,
    }));
  } else if (type.isLead()) {
    return cache.leads.all.map((x) => ({
      id: x.id,
      name: x.businessName,
      isHidden: x.isHidden,
    }));
  }
  return [];
}

export function newPlannedSalesVisit(opts: {
  userId?: Guid | null;
  date?: LocalDate | null;
  time?: LocalTime | null;
  isPlannedTimeFlexible?: boolean;
  associatedEntityType?: EnumSalesVisitAssociatedEntityType;
  associatedEntityId?: Guid | null;
}) {
  const visit = new PlannedSalesVisit();
  visit.id = Guid.create();
  visit.userId = opts.userId ?? Guid.createEmpty();
  visit.date = opts.date ?? tenantService.today;
  visit.plannedTime = opts?.time ?? LocalTime.NOON;
  visit.isPlannedTimeFlexible = !!opts.isPlannedTimeFlexible;
  visit.durationMinutes =
    tenantStore.state.tenantSettings.salesVisitDefaultMinutes;
  visit.associatedEntityType =
    opts.associatedEntityType ?? EnumSalesVisitAssociatedEntityType.outlet;
  visit.associatedEntityId = opts.associatedEntityId ?? null;

  return visit;
}
